import { browserIsTypeSupported } from '../../animationRender';
import { animationAvailability, animationUnavailable } from '../animation/availability';
import { animationCapabilityDetails } from '../animation/catalog';
import { getPreviewArtifactStore } from '../artifactStore';
import { capabilityIndex, capabilitySpec, CAPABILITY_TOPICS } from '../capabilitySpecs';
import { createInvocationId, logTemplateWebMCP } from '../logging';
import { mcpToolResult, toolError, toolSuccess, type TemplateWebMCPToolResult } from '../responses';
import type { TemplateToolContext } from '../toolContext';

export const TEMPLATE_BUILDER_CAPABILITY_TOOL = {
  name: 'template_builder_get_capability_info',
  description:
    'Return a short Template Builder recipe. Omit topic for the index. Topics are core-editing, data-bindings, brand, and animation. animation is listed in the index only when cm-template-webmcp and cm-template-builder-animations are on for a Figma template; it returns presets, parameters, units, limits, easings, and preview formats. A topic returns the read types and apply operations to call. Grouping is listed as unsupported. Returns { ok, data?, error? }. Error code is UNAUTHORIZED, INVALID_INPUT, CAPABILITY_UNAVAILABLE or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Template Builder capability',
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {
      topic: { type: 'string', enum: CAPABILITY_TOPICS },
    },
    additionalProperties: false,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: { type: 'object' },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export async function executeTemplateBuilderCapability(
  context: TemplateToolContext,
): Promise<TemplateWebMCPToolResult<Record<string, unknown>>> {
  const { canEdit, input, signal } = context;
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_CAPABILITY_TOOL.name,
    invocationId,
  });

  const finish = (
    result: TemplateWebMCPToolResult<Record<string, unknown>>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_CAPABILITY_TOOL.name,
      invocationId,
      outcome,
      durationMs: Date.now() - startedAt,
    });
    return result;
  };

  try {
    if (signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }
    if (!canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }
    if (input != null && !isRecord(input)) {
      return finish(
        mcpToolResult(
          toolError('INVALID_INPUT', 'template_builder_get_capability_info expects an object.'),
        ),
        'validation_failure',
      );
    }
    const availability = animationAvailability(context);
    const topic = isRecord(input) ? input.topic : undefined;
    if (topic == null) {
      return finish(
        mcpToolResult(toolSuccess({ topics: capabilityIndex(availability) })),
        'success',
      );
    }
    if (typeof topic !== 'string') {
      return finish(
        mcpToolResult(toolError('INVALID_INPUT', 'topic must be a string.')),
        'validation_failure',
      );
    }
    const spec = capabilitySpec(topic);
    if (!spec) {
      return finish(
        mcpToolResult(toolError('INVALID_INPUT', `Unknown topic "${topic}".`)),
        'validation_failure',
      );
    }
    if (spec.topic === 'animation') {
      if (!availability.animationsEnabled) {
        return finish(mcpToolResult(animationUnavailable()), 'validation_failure');
      }
      return finish(
        mcpToolResult(
          toolSuccess({
            ...spec,
            animation: animationCapabilityDetails({
              isTypeSupported: browserIsTypeSupported(),
              artifactUrlAvailable: getPreviewArtifactStore() != null,
            }),
          }),
        ),
        'success',
      );
    }
    return finish(mcpToolResult(toolSuccess(spec)), 'success');
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
